import type { RoleItem, UserDetail, UserListItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { errorMessage, get, post } from "../../api";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, PageHeader, Panel, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";

export const statusBadge = (s: UserListItem["status"]) =>
  s === "active" ? <Badge tone="ok">Active</Badge> : s === "invited" ? <Badge tone="wait">Invited</Badge> : <Badge tone="off">Deactivated</Badge>;

export function UsersPage() {
  const { me } = useSession();
  const staff = useQuery({ queryKey: ["users"], queryFn: () => get<UserListItem[]>("/api/users") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles") });
  const [inviting, setInviting] = useState(false);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const roleName = (id: string) => roles.data?.find((r) => r.id === id)?.name ?? "";

  const list = (staff.data ?? []).filter((s) => showDeactivated || s.status !== "deactivated");
  const deactivatedCount = (staff.data ?? []).filter((s) => s.status === "deactivated").length;

  return (
    <div>
      <PageHeader
        title="Users"
        description="Everyone who can sign in with their Google account. Roles decide what each person can see and do."
        action={
          <Button variant="primary" onClick={() => setInviting(true)}>
            Add user
          </Button>
        }
      />

      {staff.isLoading ? (
        <Loading />
      ) : staff.error ? (
        <Alert>{errorMessage(staff.error)}</Alert>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Name</th>
                <th>Roles</th>
                <th className="hidden sm:table-cell">Status</th>
                <th className="hidden md:table-cell">Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="hover:bg-mist/50">
                  <td>
                    <Link to={`/settings/users/${s.id}`} className="font-semibold hover:text-brand">
                      {s.firstName} {s.lastName}
                    </Link>
                    {s.id === me?.user.id && <span className="ml-2 text-sm text-muted">(you)</span>}
                    <div className="text-sm text-muted">{s.email}</div>
                    <div className="mt-1 sm:hidden">{statusBadge(s.status)}</div>
                  </td>
                  <td className="text-ink-soft">{s.roleIds.map(roleName).filter(Boolean).join(", ") || "No role"}</td>
                  <td className="hidden sm:table-cell">{statusBadge(s.status)}</td>
                  <td className="hidden text-sm text-ink-soft md:table-cell">{s.lastSignInAt ? formatDateTime(s.lastSignInAt, me?.company.timezone) : "Not yet"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {deactivatedCount > 0 && (
        <button className="mt-4 text-sm font-medium text-muted hover:text-ink" onClick={() => setShowDeactivated((v) => !v)}>
          {showDeactivated ? "Hide" : "Show"} deactivated users ({deactivatedCount})
        </button>
      )}

      {inviting && roles.data && <InviteDialog roles={roles.data} onClose={() => setInviting(false)} />}
    </div>
  );
}

function InviteDialog({ roles, onClose }: { roles: RoleItem[]; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", phone: "" });
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const invite = useMutation({
    mutationFn: () => post<UserDetail>("/api/users", { ...form, phone: form.phone || null, roleIds }),
    onSuccess: async (d) => {
      await qc.invalidateQueries({ queryKey: ["users"] });
      await qc.invalidateQueries({ queryKey: ["users-directory"] });
      onClose();
      navigate(`/settings/users/${d.id}`);
    },
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog
      title="Add user"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" form="invite-form" type="submit" busy={invite.isPending} disabled={!roleIds.length}>
            Add user
          </Button>
        </>
      }
    >
      <form
        id="invite-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}
      >
        {invite.error && <Alert>{errorMessage(invite.error)}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" required value={form.firstName} onChange={set("firstName")} autoFocus />
          <Field label="Last name" required value={form.lastName} onChange={set("lastName")} />
        </div>
        <Field label="Email" type="email" required value={form.email} onChange={set("email")} hint="Their Google Workspace email. They sign in with Google; no password to send." />
        <Field label="Mobile (optional)" type="tel" value={form.phone} onChange={set("phone")} />
        <fieldset>
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Roles</legend>
          <div className="flex flex-col">
            {roles.map((r) => (
              <Checkbox
                key={r.id}
                label={r.name}
                description={r.description}
                checked={roleIds.includes(r.id)}
                onChange={(on) => setRoleIds((ids) => (on ? [...ids, r.id] : ids.filter((x) => x !== r.id)))}
              />
            ))}
          </div>
        </fieldset>
      </form>
    </Dialog>
  );
}
